import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, map } from 'rxjs/operators';
import { OeAppService } from './oe-app.service';
import { Store } from './store';
import ConfigurationsDto, { NotificationProvider } from './types/ConfigurationsDto';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProfileServiceConfigurationService extends Store<ConfigurationsDto> {
  constructor(http: HttpClient, oeApp: OeAppService) {
    super();
    http
      .get<ConfigurationsDto>(`${oeApp.getCommonData().oeProfileServiceHost}/configurations`)
      // fall back to an empty config so filter-until-config consumers still emit during an outage
      .pipe(catchError(() => of({} as ConfigurationsDto)))
      .subscribe((config) => {
        this.setState(config);
      });
  }

  isSpeechaceCarouselEnabled$() {
    return this.state$.pipe(map((config) => config && config.speechaceCarouselEnabled));
  }

  isPCRevampEnabled$() {
    return this.state$.pipe(map((config) => config && config.pcRevampDesktopUIEnabled));
  }

  isPCReservationEnabled$(): Observable<boolean> {
    return this.loadedFlag$('pcReservationEnabled');
  }

  coachMobileUIEnabled$() {
    return this.state$.pipe(map((config) => config && config.coachMobileUIEnabled));
  }

  isExtoleEnabled$() {
    return this.state$.pipe(map((config) => config && config.isExtoleEnabled));
  }

  isLiveClassOnboardingEnabled$() {
    return this.state$.pipe(map((config) => config && config.liveClassOnboardingEnabled));
  }

  isPlatformTourGCEnabled$() {
    return this.state$.pipe(map((config) => config && config.isPlatformTourGCEnabled));
  }

  liveClassPromoTileEnabled$() {
    return this.state$.pipe(map((config) => config && config.liveClassPromoTileEnabled));
  }

  yearInReviewEnabled$() {
    return this.state$.pipe(map((config) => config && config.yearInReviewEnabled));
  }

  hybridClassTypeDesktopUIEnabled$() {
    return this.state$.pipe(map((config) => config && config.hybridClassTypeDesktopUIEnabled));
  }

  notificationPushConfig$(): Observable<{
    notificationWebPushProvider: NotificationProvider;
    oneSignalAppId: string;
  }> {
    return this.state$.pipe(
      map(
        (config) =>
          config && {
            notificationWebPushProvider: config.notificationWebPushProvider,
            oneSignalAppId: config.oneSignalAppId,
          }
      )
    );
  }

  englishCentralEnabled$() {
    return this.state$.pipe(map((config) => config && config.englishCentralEnabled));
  }

  liveClassMaxAttendeesForChat$(): Observable<number> {
    return this.state$.pipe(map((config) => config && config.liveClassMaxAttendeesForChat));
  }

  liveClassPlayerSessionLockEnabled$(): Observable<boolean> {
    return this.state$.pipe(map((config) => config && config.liveClassPlayerSessionLockEnabled));
  }

  unitQuizCooldownPeriodHours$(): Observable<number | undefined> {
    return this.state$.pipe(
      filter((config) => !!config),
      map((config) => config.unitQuizCooldownPeriodHours)
    );
  }

  hybridAfterLiveClassPracticeEnabled$(): Observable<boolean> {
    return this.state$.pipe(map((config) => config && config.hybridAfterLiveClassPracticeEnabled));
  }

  // waits for config to load before emitting so the lesson player isn't chosen on undefined config
  aiLessonPlayerForMiniLessonEnabled$(): Observable<boolean> {
    return this.loadedFlag$('aiLessonPlayerForMiniLessonEnabled');
  }

  // waits for config to load so the ISC modal doesn't render the legacy player before the flag arrives
  iscMonorepoUIEnabled$(): Observable<boolean> {
    return this.loadedFlag$('iscMonorepoUIEnabled');
  }

  isInteractiveVideosCarouselEnabled$(): Observable<boolean> {
    return this.loadedFlag$('interactiveVideosCarouselEnabled');
  }

  // consumers must not decide on the undefined config that precedes the load
  private loadedFlag$(flag: keyof ConfigurationsDto): Observable<boolean> {
    return this.state$.pipe(
      filter((config) => !!config),
      map((config) => !!config[flag])
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/profile-service-configuration.service.ts